/* Staff — the roster, including the column the console never shows.
 *
 * Who works at this salon and what they do. The console has this page too, so
 * most of it is the same list; the two differences are the whole reason to have
 * it here:
 *
 *   external_staff_id  what the booking system calls this person. The console
 *                      writes it when a turn is assigned from the book and never
 *                      shows it, so a mismatched id is invisible from inside the
 *                      salon and presents as "the board keeps putting bookings
 *                      on the wrong technician". This is where it gets fixed.
 *   delete for real    the console's Remove archives — `active = false`, history
 *                      intact. A row can also be deleted outright, which takes
 *                      every turn that person ever had with it. Both are here,
 *                      and they are labelled as what they are.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';
import { ConfirmButton } from '../components/confirm-button.js';
// The hotkey is optional, so the disc on a chip cannot be the key — see
// ../rules/services.js for the fallback the board uses.
import { serviceAbbrev } from '../rules/services.js';

var Staff = {
  name: 'AdminStaff',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var newName = Vue.ref('');
    var showArchived = Vue.ref(false);

    var services = Vue.computed(function () {
      return store.data.services.filter(function (s) { return s.active !== false; });
    });

    var roster = Vue.computed(function () {
      return store.data.staff.filter(function (s) { return s.active !== false; });
    });

    var archived = Vue.computed(function () {
      return store.data.staff.filter(function (s) { return s.active === false; });
    });

    async function patch(row, changes) {
      var id = store.selectedId.value;
      var res = await api.updateRow('tt_staff', id, { id: row.id }, changes);
      if (!res) return;
      Object.assign(row, changes);
      api.log(id, 'staff.update', { staff: row.id, changes: changes });
    }

    // The service list is an array column, so a toggle is a whole-array write.
    // Read from the row rather than from a local copy: two admins on the same
    // salon is unlikely, but a stale array here would silently drop somebody's
    // other services.
    function toggleService(row, serviceId) {
      var ids = (row.service_ids || []).slice();
      var i = ids.indexOf(serviceId);
      if (i === -1) ids.push(serviceId); else ids.splice(i, 1);
      patch(row, { service_ids: ids });
    }

    async function add() {
      var name = newName.value.trim();
      var id = store.selectedId.value;
      if (!name) { api.toast('Enter a name first.', 'bad'); return; }

      /* The id is generated here rather than left to the column default, which
       * is what the console does too — the board creates a row's id at the
       * moment of the action so the same write can be replayed from its durable
       * queue. There is no queue in this app; the reason to keep it is that the
       * new row can be pushed into the list without a round trip to find out
       * what it was called. */
      var row = {
        id: (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : undefined,
        name: name,
        service_ids: [],
        sort_order: (store.data.staff.length + 1) * 10,
        active: true
      };
      var res = await api.writeRow('tt_staff', id, row);
      if (!res) return;
      store.data.staff.push(res[0] || row);
      newName.value = '';
      api.log(id, 'staff.add', { name: name });
    }

    async function archive(row) {
      await patch(row, { active: false });
      api.toast('Archived. Their turns are untouched and they can be restored.');
    }

    async function destroy(row) {
      var id = store.selectedId.value;
      var res = await api.deleteRows('tt_staff', id, { id: row.id });
      if (!res) return;
      store.data.staff = store.data.staff.filter(function (s) { return s.id !== row.id; });
      api.log(id, 'staff.delete', { staff: row.id, name: row.name });
      api.toast('Deleted, along with every turn ever recorded against them.');
      // The overview's counts are now wrong, and this is one of the few actions
      // that changes them enough to matter.
      store.loadTenants();
    }

    function move(row, delta) {
      var list = roster.value;
      var i = list.indexOf(row);
      var j = i + delta;
      if (j < 0 || j >= list.length) return;
      // Swap the two sort_orders rather than renumbering the list: fewer writes,
      // and the console reads this column with no expectation that it is dense.
      var a = row.sort_order, b = list[j].sort_order;
      patch(row, { sort_order: b });
      patch(list[j], { sort_order: a });
    }

    return {
      roster: roster,
      archived: archived,
      services: services,
      showArchived: showArchived,
      newName: newName,
      add: add,
      patch: patch,
      toggleService: toggleService,
      letterFor: function (svc) {
        return serviceAbbrev(svc.abbrev, svc.name, svc.shortcut);
      },
      archive: archive,
      destroy: destroy,
      move: move,
      loading: Vue.computed(function () { return store.data.loading; }),
      goPage: nav.goPage
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Staff</h2>',
    '    <p class="page__lead">Who works here and which services each of them performs.',
    '      Everything about <strong>today</strong> &mdash; who is in, the column order, how',
    '      many turns each has &mdash; belongs to the board and is not here.</p>',
    '  </div>',
    '',
    '  <form class="card" @submit.prevent="add">',
    '    <h3>Add a technician</h3>',
    '    <div class="card__row">',
    '      <input v-model="newName" class="input" type="text" maxlength="40" placeholder="Name" />',
    '      <button type="submit" class="btn btn--primary">Add</button>',
    '    </div>',
    '    <p class="hint">Added with no services against them, which means the board will not',
    '      offer them anything until some are ticked below.</p>',
    '  </form>',
    '',
    '  <p v-if="loading" class="empty">Loading…</p>',
    '  <p v-else-if="!roster.length" class="empty">No technicians on this salon&rsquo;s roster.</p>',
    '',
    '  <ul class="rows">',
    '    <li v-for="(s, i) in roster" :key="s.id" class="row">',
    '      <div class="row__order">',
    '        <button type="button" class="icon-btn" :disabled="i === 0" @click="move(s, -1)"',
    '          aria-label="Move up">&#9650;</button>',
    '        <span class="row__pos">{{ i + 1 }}</span>',
    '        <button type="button" class="icon-btn" :disabled="i === roster.length - 1"',
    '          @click="move(s, 1)" aria-label="Move down">&#9660;</button>',
    '      </div>',
    '',
    '      <div class="row__main">',
    '        <div class="row__top">',
    '          <label class="field field--grow">',
    '            <span class="field__label">Name</span>',
    '            <input class="input" type="text" :value="s.name" maxlength="40"',
    '              @change="patch(s, { name: $event.target.value })" />',
    '          </label>',
    '          <label class="field">',
    '            <span class="field__label">Booking system id</span>',
    '            <input class="input input--id" type="text" :value="s.external_staff_id || \'\'"',
    '              placeholder="—" spellcheck="false"',
    '              title="What the booking system calls this person. The console writes it and never shows it."',
    '              @change="patch(s, { external_staff_id: $event.target.value.trim() || null })" />',
    '          </label>',
    '        </div>',
    /* The default for every day. The desk can change it for one day from the
       right-click on the name in the console; that does not come back here. */
    '        <label class="switch">',
    '          <input type="checkbox" :checked="!!s.own_clients_only"',
    '            @change="patch(s, { own_clients_only: $event.target.checked })" />',
    '          <span class="switch__track"><span class="switch__thumb"></span></span>',
    '          <span class="switch__label">Own clients only &mdash; on the board for clients who ask',
    '            for them, out of the rotation for everybody else</span>',
    '        </label>',
    '        <div class="chips">',
    '          <button v-for="svc in services" :key="svc.id" type="button" class="chip"',
    '            :class="{ \'chip--on\': (s.service_ids || []).includes(svc.id) }"',
    '            :style="{ \'--chip\': svc.color }" @click="toggleService(s, svc.id)">',
    '            <span class="chip__letter">{{ letterFor(svc) }}</span>',
    '            <span class="chip__name">{{ svc.name }}</span>',
    '          </button>',
    '          <span v-if="!services.length" class="hint hint--inline">',
    '            No services yet &mdash; add them under Services first.</span>',
    '        </div>',
    '      </div>',
    '',
    '      <div class="row__end">',
    '        <confirm-button label="Archive" confirm="Archive (keeps history)" size="sm"',
    '          kind="quiet" @confirmed="archive(s)" />',
    '        <confirm-button label="Delete" size="sm"',
    '          confirm="Delete and lose every turn"',
    '          @confirmed="destroy(s)" />',
    '      </div>',
    '    </li>',
    '  </ul>',
    '',
    '  <div v-if="archived.length" class="card">',
    '    <button type="button" class="link-btn" @click="showArchived = !showArchived">',
    '      {{ showArchived ? "Hide" : "Show" }} archived staff ({{ archived.length }})</button>',
    '    <ul v-if="showArchived" class="rows rows--tight">',
    '      <li v-for="s in archived" :key="s.id" class="row row--slim">',
    '        <span class="row__name">{{ s.name }}</span>',
    '        <span class="row__end">',
    '          <button type="button" class="btn btn--quiet btn--sm"',
    '            @click="patch(s, { active: true })">Restore</button>',
    '          <confirm-button label="Delete" confirm="Delete and lose every turn" size="sm"',
    '            @confirmed="destroy(s)" />',
    '        </span>',
    '      </li>',
    '    </ul>',
    '  </div>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'staff',
  name: 'Staff',
  group: 'Console settings',
  page: Staff
});

export { Staff };
